import { useMemo } from 'react'

import { useApp }     from '../../../app/AppContext.jsx'
import { useCatalog } from '../../../domains/catalog/context/CatalogContext.jsx'

import { useI18n } from '../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { buildCompareColumnRows, buildCompareRowVisibility } from '../utils/compareRows.js'

import { Button } from '../../../shared/ui/Button/index.js'
import { Close } from '../../../shared/ui/icons/Close.jsx'
import { InfoTooltip } from '../../../shared/ui/InfoTooltip/InfoTooltip.jsx'
import { Logger } from '../../../shared/utils/logger.js'
import { OfferBadge } from '../../../shared/ui/OfferBadge/OfferBadge.jsx'
import { Pin } from '../../../shared/ui/icons/Pin.jsx'
import { StatusBadge } from '../../../shared/ui/StatusBadge/index.js'
import { UnitPlanThumb } from '../../../shared/ui/UnitPlanThumb/index.js'

import styles from './FavoritesCompareBlock.module.scss'

/**
 * Single column in the compare section — one pinned product with its compare items.
 */
export function CompareColumn({
  product,
  compareRows = [],
  projectDataUrl = null,
  compartmentTypes = [],
  logoSrc = null,
  use3dThumbs = false,
  pricesVisibility = null,
  onClose = () => {},
  onOpenProduct = () => {},
}) {
  const { t, plural } = useI18n()
  const getProductsCategoryString = useProductStrings()

  // ── Context reads ────────────────────────────────────────────────────────────
  const { panelSettings, projectConfig } = useApp()
  const { buildings, compartments } = useCatalog()
  
  // ── Derived state ────────────────────────────────────────────────────────────
  const appConfig = projectConfig?.app_config
  const arePricesVisible = pricesVisibility?.pricesVisible !== false
  // const pricesVisibility = parsePricesVisibility(panelSettings); //priceUtils.js

  const visibilityMap = useMemo(
      () => buildCompareRowVisibility(
      panelSettings, 
      appConfig, 
      arePricesVisible, 
      buildings),
      [panelSettings, appConfig, arePricesVisible, buildings],
    )

  const rows = useMemo(
    () => buildCompareColumnRows(
      product, visibilityMap, t, plural, { 
      panelSettings, 
      pricesVisibility, 
      compartmentTypes: compartments?.compartments_types,
      }),
    [product, visibilityMap, t, plural, panelSettings, pricesVisibility, compartments],
  )

  const idOriginal = product?.id_original ?? product.cms_product_name ?? product?.id_cad ?? null

  const eventDetail = {
    units: [{
      investmentId: product?.crm_id_investment ?? null,
      stageId: product?.crm_id_stage ?? null,
      buildingId: product?.crm_id_building ?? null,
      unitId: product?.crm_id_unit ?? product?.number ?? product?.id_cad ?? null,
      unitName: idOriginal,
      userAddedItems: null
    }]
  };

  const onInquiry = (product) => {
    Logger.info({
      message: 'FavoritesCompareBlock cadaplusProductQueryClick event dispatched',
      content: `${JSON.stringify(eventDetail)}`
    })
    const event = new CustomEvent('cadaplusProductQueryClick', {
      detail: {
        ...eventDetail
      },
      bubbles:  true,
      composed: true,
    })
    document.dispatchEvent(event)
  }


  if (!product) return null

  return (
    <div className={styles.compareColumn}>
      {/* Header with product ID and close button */}
      <div className={styles.columnHeader}>
        <span className={styles.productId}>{product.id_original}</span>
          <Button
            type="button"
            kind="icon"
            size="small"
            aria-label={t('common.close')}
            onClick={() => onClose(product.uuid)}
          >
            <Close />
          </Button>
      </div>

      {/* Rows */}
      <div className={styles.columnRows}>
        {rows.map((row) => (
          <div key={row.id} className={styles.compareElement} data-item={row.id}>
            {/* Special render: Plan thumb */}
            {row.component === 'plan_thumb' && (
              <div className={styles.compareThumbCell}>
              <UnitPlanThumb
                projectDataUrl={projectDataUrl}
                product={product}
                logoSrc={logoSrc}
                use3dVersion={use3dThumbs}
              />
              </div>
            )}

            {/* Special render: Status badge + Offer badge */}
            {row.component === 'status' && (
              <div className={styles.compareStatusCell}>
                <StatusBadge status={product.status} size="normal" />
                {product.price_offer?.on_offer && (
                  <OfferBadge size="normal" />
                )}
              </div>
            )}

            {/* Special render: Price - current and strikethrough values when on offer*/}
            {row.component === 'price' && (
              <div className={styles.compareDataCell}>
                {row.label && (
                  <div className={styles.cellLabel}>{row.label}</div>
                )}
                <div className={styles.priceValues}>
                  {product.price_offer?.on_offer && row.value.strikethroughPrice && (
                    <div className={styles.strikethrough}>{row.value.strikethroughPrice}</div>
                  )}
                  <div className={styles.cellValue}>{row.value.displayPrice}</div>
                </div>
              </div>
            )}

            {/* Special render: Open product CTA */}
            {row.component === 'action' && (
              <Button
                type="button"
                kind="wide"
                size="large"
                onClick={() => onOpenProduct(product)}
              >
                {/* {getProductsCategoryString('sheet', product.product_category)} */}
                {/* {getProductsCategoryString('sheet', row.value)} */}
                {row.value}
              </Button>
            )}

            {/* Special render: Inquiry CTA */}
            {row.component === 'cta' && (
              <Button
                type="button"
                kind="wide"
                size="large"
                cta
                onClick={() => onInquiry(product)}
              >
                {row.value}
              </Button>
            )}

            {/* Generic item: label + value */}
            {!row.component && (
              <div className={styles.compareDataCell}>
                {row.label && (
                  <div className={styles.cellLabel}>
                    {row.label}
                    {row.tooltip && <InfoTooltip text={row.tooltip.text} />}
                  </div>
                )}
                <div className={styles.cellValue}>{row.value}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Placeholder card when pinned < max — encourages pinning more products.
 */
export function PlaceholderCard({ maxPinned = 3 }) {
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings()

  return (
    <div className={styles.placeholderCard}>
      <Pin className={styles.placeholderIcon} />
      <div className={styles.placeholderText}>
        {getProductsCategoryString("pin_to_compare", 'default')}
      </div>
    </div>
  )
}
